import { Component } from '@angular/core';
import { DataLayerBaseComponent } from './data-layer-base.component';
import { DataLayerGeojsonPointComponent } from './data-layer-geojson-point.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';
import { DataWebServicesService } from './data-web-services.service';
declare var L: any;

/*
 * data-layer-geojson-point-weather-station defines a class to display weather stations.  The data format is geojson.
 *
 * Example of the data file:
 * --------------------------------
 * {"type": "FeatureCollection",
 *	"crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:OGC:1.3:CRS84" } },
 *	"product":"NWS Warnings at [2018 03/23 03:00:01 UTC]",
 *	"created_by":"Ventra County Watershed Protection District and TriLynx Systems",
 *	"created_from":"https://forecast.weather.gov/wwamap/wwatxtget.php",
 *	"features": [
 *	{"type": "Feature", "properties": { "Class":"SVR", "Name":"Severe Weather Statement",
		"StationName": "Weather Station 1234", "Temperature":"82", "DewPoint":"42", "WindDirection":"NE",
		"WindSpeed":"12", "WindGust":"15", "Description":
		"Severe Weather Statement\nNational Weather Service Wichita KS\n521 PM CDT TUE  ...  \n\nMCGUIRE\n"
 *	}, "geometry": { "type": "Point", "coordinates": [-105.575071,39.4213]}},
 *	  ...
 *	]
 * }
 * --------------------------------
 *
 * Within the configuration file, weather stations can be included:
 * "overlayLayers": [
 * 		{"id": "weatherStationLayer", "label": "Weather Stations", "url": "map/overlay-layers/weatherStations.geojson",
 *		 "class": "data-layer-geojson-point-weather-station", "refresh": false },
 * ...
 * ]
 */

@Component({
	selector: 'data-layer-geojson-point-weather-station',
	standalone: false,
	template: `
		<div class="checkboxDiv"><label class="overlay-layer"><input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}</label>
		<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css', 'data-layer-templates.css']
})
export class DataLayerGeojsonPointWeatherStationComponent extends DataLayerGeojsonPointComponent {

	dataTypes: any[] = [];
	stationFilterType: string[] = ["TempAir"];
	stationFilterTypeLogicalOperator: string = "or";
	descriptionProperty: string|null = null;

	constructor(
		mapService: MapService,
		dataLayerService: DataLayerService,
		configService: ConfigService,
		userMessageService: UserMessageService,
		dataWebServices: DataWebServicesService ) {
		super(mapService, dataLayerService, configService, userMessageService);
	}

	setData ( someData: any ) {
		// Set the standard data
		super.setData(someData);

		// Set data unique to DWS
		if ( someData.hasOwnProperty('dataTypes')) {
			this.dataTypes = someData.dataTypes;
		}
		
		// station filter type AND/OR descriptionProperty - this indicates the station is a "weather station"
		if ( someData.hasOwnProperty('stationFilters')) {
			const stationFilters = someData.stationFilters;

			if ( stationFilters.hasOwnProperty('descriptionProperty')) {
				this.descriptionProperty = stationFilters.descriptionProperty;
			}
			if ( stationFilters.hasOwnProperty('stationFilterType')) {
				this.stationFilterType = stationFilters.stationFilterType;
			}
			if ( stationFilters.hasOwnProperty('stationFilterTypeLogicalOperator')) {
				const op = stationFilters.stationFilterTypeLogicalOperator;
				if ( op == "and ") {
					this.stationFilterTypeLogicalOperator = op;
				}
				// otherwise leave it set to "or" - no other values are allowed
			}
		}
	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
			this.activateLayer();
		} else {
			this.deactivateLayer();
		}
	}

	// refreshLayer first checks to see if the layer is active.  If so, activateLayer is called.
	refreshLayer() {
		if ( this.active === true ) {
			this.activateLayer();
		}
	}

	// first check whether the information has been read.
	// display the markers on the map and set the layer to active
	activateLayer () {
		console.log('Activating ' + this.id + ' layer.');
		if ( this.mapLayer != null ) {
				this.mapService.map.removeLayer(this.mapLayer);
		}
		if ( this.value == null || this.refresh === true ) {
			// once the data file has been read, drawMarkers is automatically called because an observer has been established

			this.parseDataFile();
		} else {
			this.drawMarkers();
		}

		this.active = true;
	}

	drawMarkers () {
		console.log('Drawing ' + this.value.features.length + ' stations.');
		this.mapLayer = L.geoJson(this.value, {
			// placeMarkers using configuration which specifies which property should be used as the title, popup, etc.
			pointToLayer: this.placeMarkers,
			onEachFeature: (feature: any, layer: any) => this.onEachPointFeature(feature,layer)
		}).addTo(this.mapService.map);
	}

	// create popup and tooltip
	onEachPointFeature(feature, layer) {
		const stationSummary = feature.properties.stationSummary;
		const stationName = stationSummary.name;
		const sss = stationSummary.statistics.filter((ss: any) => {
				return (ss.dataType == "VoltageBattery" );
			});
		const lastReportStat: any = this.dataLayerService.getLastReportStatistic ( sss, 'Last' );
		// add last report date
		const periodEndString = lastReportStat != null ?
			"Current time used to compute statistics: " + 
			this.dataLayerService.dateToISOButLocal(lastReportStat.periodEnd) + "<br>" : "";

		const lastReportStatString = lastReportStat != null ?
			'Time of last report: ' + 
			this.dataLayerService.dateToISOButLocal( lastReportStat.statisticDateTime) + '<br>': "";

		// create a table
		let summaryString = '<table class="tt">';

		// header
		summaryString += "<tr><th>Statistic</th><th>15 Minutes</th><th>1 Hour</th><th>3 Hours</th><th>1 Day</th><th>7 Days</th>";
		const intervals = ['15Minute', '1Hour', '3Hour', '1Day', '7Day'];

		// data
		this.dataTypes.forEach((dt: any, index: number) => {
			// filter stationSummary for data type and statistic type
			const filteredStationSummary = stationSummary.statistics.filter((ss: any) => {
				return (ss.dataType == dt.dataType && ss.statisticType == dt.statisticType );
			});
			summaryString += "<tr><td>" + dt.dataType + " " + dt.statisticType + "</td>";
			// get each interval
			intervals.forEach((int: any) => {
				const intervalFilteredStationsSummary = filteredStationSummary.filter((fss: any) => {
					return fss.duration == int;
				});
				if ( intervalFilteredStationsSummary.length > 0 && intervalFilteredStationsSummary[0].statisticValue != null ) {
					const stat = intervalFilteredStationsSummary[0];

					// determine if this is the configured requested statistidc
					// put in bold if so
					const bold = dt.duration == stat.duration;
					let statString = stat.statisticValue + " " + stat.statisticUnits;

					// wind speed is special, need wind direction too
					if ( dt.dataType == "WindSpeed") {
						const statisticsArray2 = stationSummary.statistics.filter((f: any) => {
							return f.dataType == "WindDir" && f.duration == dt.duration && f.statisticType ==dt.statisticType
						})
						if ( statisticsArray2.length > 0 ) {
							if ( statisticsArray2[0].statisticValue != null ) {
								const winddir = this.mapService.getWindString(statisticsArray2[0].statisticValue);
								statString += (" " + winddir );
							}
						}
					}

					// add statistic to the table
					summaryString += '<td class="' + (bold ? 'emphasizedCell' : '') + '">' + statString + '</td>';
				} else {
					// no statistic, just add dash
					summaryString += "<td>-</td>";
				}
			})
			summaryString += "</tr>";

			/*
			summaryString += "<tr><td>" + dt.dataType + " " + dt.statisticType + 
				"</td><td>" + dt.duration + "</td><td>" + stationSummary.weatherStationStatistics[index] + "</td>";
				*/
		})
		summaryString += "</table>";

		const options = { maxHeight: 500, minWidth: 500 };
		const preTooltipContent = '<span class="bold">' + stationName + '</span><br>' + periodEndString + summaryString + lastReportStatString;
			
		const tooltipContent = preTooltipContent + '<br>Click marker for more information';

		let popupContent = preTooltipContent +
							"</p> <button class=\"popupButton\" onclick='globalMapComponent.gotoDetail(" +
							feature.properties.stationSummary.numId +
							")'>Go to Station Dashboard</button>";


		/*
		if (feature.properties && feature.properties.popupContent) {
			// This will append content from a "popupContent" property if it is in the GeoJSON file
			popupContent += feature.properties.popupContent;
		}
			*/

		layer.bindPopup(popupContent, options);
		layer.bindTooltip(tooltipContent);
	}

	// markers on map
	placeMarkers(feature, latlng) {
		const stationSummary = feature?.properties?.stationSummary;
		const weatherStationStatistics = stationSummary?.weatherStationStatistics;

		const circleRadius = 5;
		const svgWidth = 150;
    	const numLines = Math.ceil(weatherStationStatistics.length / 2);
		const svgHeight = (numLines*20) + circleRadius;

		let weatherStationStatisticsString = "";
		for (let j = 0; j < weatherStationStatistics.length; j++) {
			let x;

			// If this is the last element and the array has an odd number of elements,
			// center it.
			if (
				weatherStationStatistics.length % 2 === 1 &&
				j === weatherStationStatistics.length - 1
			) {
				x = svgWidth/3;
			}
			// Otherwise alternate between left and right.
			else {
				x = (j % 2 === 0) ? (circleRadius*3) : ((5/9)*svgWidth);
			}

			// Increment y every two items.
    		let y = 1 + Math.floor(j / 2) * 17;

			// const specificNum = (j % 6) + 1;
			const specificNumClass =  "weatherStationNumbersNum" + "4";  // specificNum;
			weatherStationStatisticsString +=
				'<rect x="' + x + '" y="' + y + 
				'" width="80" height="14" rx="3" ry="3" stroke="black" stroke-width="0" fill="transparent"/>' +
        		'<text class="weatherStationNumbers ' + specificNumClass + '" x="' + x + '" y="' + (y + 13) + '" stroke="black">' +
        		'<tspan>' + weatherStationStatistics[j] + '</tspan>' +
        		'</text>';
		}

		//
		const fullWeatherStationString = '<svg width="' + svgWidth + '" height="' + svgHeight + '"> ' +
				'<circle r="' + circleRadius + '" cx="' + svgWidth/2 + '" cy="' + (svgHeight-circleRadius) + '" fill="purple" />' +
				'<style> .weatherNumbers {font-family: arial; font-size: 16px; font-weight:100 }</style> ' +
				weatherStationStatisticsString +
				'</svg>';
		
		const marker = L.divIcon({
				iconAnchor: [(svgWidth/2), svgHeight],
				html: fullWeatherStationString
		});

		marker.options.shadowSize = [50, 75];
		// marker.options.title = "hello";

		return L.marker(latlng, {icon: marker});
	}

	deactivateLayer() {
		console.log('Deactivating ' + this.id + ' layer.');
		if ( this.active === true ) {
			this.mapService.map.removeLayer(this.mapLayer);
			this.active = false;
		}

	}

	// from data-layer-service.parseCameraStations
	parseDataFile () {
		console.log('In data-layer-geojson-point-weather.parseDataFile.');
		const self = this;

		const requestStations = new XMLHttpRequest();
		requestStations.onload = function ( ) {
			if ( requestStations.readyState === requestStations.DONE && requestStations.status === 200 ) {

				// first, get json file content
				let allStations = JSON.parse(requestStations.responseText);
				// console.log(allStations);

				//
				// filter the features to include only wether stations
				//
				if ( self.descriptionProperty ) {
					// filter the features to only include those containing a specified data type
					// uses descriptionProperty
					// dp may be something like "WeatherStation"
					const dp = self.descriptionProperty;
					allStations.features = allStations.features.filter(function(s) {
						const description = s.properties.stationSummary.description;
						const descriptionParsed = self.configService.parseDescription(description);
						const descriptionProps = descriptionParsed['descriptionParameters'];
						const importantDP = descriptionProps.filter(function (p: any) { return p.name == dp });
						return (importantDP.length === 0|| importantDP[0]['value'] == false ) ? false : true; 
					});

				} else {
					// filter the features to only include those containing a specified data type
					// uses stationFilterType
					if ( self.stationFilterTypeLogicalOperator == "and") {
						allStations.features = allStations.features.filter(s =>
							self.stationFilterType.every(filterType =>
								s.properties.stationSummary.dataTypes.some(d => d.name === filterType)
							)
						);
					} else {
						// "or"
						allStations.features = allStations.features.filter(function(s) {
							const dt = s.properties.stationSummary.dataTypes;
							const dt_filtered = dt.filter(function (d) { return self.stationFilterType.includes(d.name); });
							return dt_filtered.length === 0 ? false : true; 
						});
					}
				}

				// console.log(allStations);

				// retrieve the weather statistics
				allStations.features.forEach((feature: any) => {

					const stationSummary = feature.properties?.stationSummary;
					const weatherStationStatistics: any[] = [];
					self.dataTypes.forEach((dt: any) => {

						const statisticsArray = stationSummary.statistics.filter((f: any) => {
							return f.dataType == dt.dataType && f.duration == dt.duration && f.statisticType ==dt.statisticType
						})
						// console.log(stationSummary);

						// get actual statistic 
						let statistic = "NA";
						if ( statisticsArray.length > 0 && statisticsArray[0].statisticValue != null ) {
							statistic = statisticsArray[0].statisticValue + " " + statisticsArray[0].statisticUnits;

							// wind speed is special, need wind direction too
							if ( dt.dataType == "WindSpeed") {
								const statisticsArray2 = stationSummary.statistics.filter((f: any) => {
									return f.dataType == "WindDir" && f.duration == dt.duration && f.statisticType ==dt.statisticType
								})
								if ( statisticsArray2.length > 0 ) {
									if ( statisticsArray2[0].statisticValue != null ) {
										const winddir = self.mapService.getWindString(statisticsArray2[0].statisticValue);
										statistic += (" " + winddir );
									}
								}
							}
						}
						weatherStationStatistics.push(statistic);
					});
					feature.properties.stationSummary['weatherStationStatistics'] = weatherStationStatistics;
				})

				self.value = allStations;
				self.valueObservable.next(allStations);
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
					filename + ' for all ' + self.id + ' stations was successfully completed.');
				// console.log ( dataLayer );

			} else {
				console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' + filename + ' for all ' + self.id + ' stations was UNSUCCESSFUL.');
			}
		};
		const filename = self.configService.createURI ( { 'filename': self.url });
		requestStations.open('get', filename, true );
		requestStations.overrideMimeType('text/plain');
		console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + filename + ' for all ' + this.id + ' stations.');
		requestStations.send();
	}

}
